import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Phone, ArrowRight, ArrowLeft, CheckCircle2, Shield, Sparkles } from 'lucide-react';
import { signIn } from '../lib/authStore';
interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  title?: string;
  subtitle?: string;
}
type Step = 'phone' | 'register' | 'otp' | 'done';
type Mode = 'signin' | 'register';
const DEFAULT_SIGNIN_NAME = 'Ahmad';
export function LoginModal({
  open,
  onClose,
  onSuccess,
  title = 'Sign in to continue',
  subtitle = 'We use your phone number to keep your moves and bids in one place.'
}: Props) {
  const [mode, setMode] = useState<Mode>('signin');
  const [step, setStep] = useState<Step>('phone');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [welcomeName, setWelcomeName] = useState('');
  const [error, setError] = useState('');
  const inputsRef = useRef<(HTMLInputElement | null)[]>([]);
  useEffect(() => {
    if (!open) {
      setMode('signin');
      setStep('phone');
      setPhone('');
      setOtp(['', '', '', '', '', '']);
      setFullName('');
      setEmail('');
      setWelcomeName('');
      setError('');
    }
  }, [open]);
  // Switching modes resets all input + step
  const switchMode = (next: Mode) => {
    setMode(next);
    setStep(next === 'register' ? 'register' : 'phone');
    setPhone('');
    setOtp(['', '', '', '', '', '']);
    setFullName('');
    setEmail('');
    setError('');
  };
  const phoneValid = phone.replace(/\D/g, '').length >= 9;
  const sendOtp = () => {
    if (!phoneValid) {
      setError('Enter a valid UAE phone number');
      return;
    }
    setError('');
    setStep('otp');
    setTimeout(() => inputsRef.current[0]?.focus(), 60);
  };
  const updateOtp = (idx: number, value: string) => {
    const v = value.replace(/\D/g, '').slice(0, 1);
    setOtp(arr => {
      const next = [...arr];
      next[idx] = v;
      return next;
    });
    if (v && idx < 5) inputsRef.current[idx + 1]?.focus();
  };
  const onOtpKey = (idx: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otp[idx] && idx > 0) inputsRef.current[idx - 1]?.focus();
  };
  const onOtpPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (!pasted) return;
    const next = ['', '', '', '', '', ''];
    for (let i = 0; i < pasted.length; i++) next[i] = pasted[i];
    setOtp(next);
    inputsRef.current[Math.min(pasted.length, 5)]?.focus();
  };
  const verifyOtp = () => {
    if (otp.some(d => d === '')) {
      setError('Enter the full 6-digit code');
      return;
    }
    setError('');
    // Sign-in mode → default "Ahmad". Register mode → use the captured name.
    const name = mode === 'register' ? fullName.trim() : DEFAULT_SIGNIN_NAME;
    setWelcomeName(name);
    signIn(`+971 ${phone}`, name || undefined);
    setStep('done');
    // Keep the personalized welcome up long enough to actually read it.
    setTimeout(() => {
      onSuccess?.();
      onClose();
    }, 2800);
  };
  // Register-form validation. Email is required for the registration path.
  const registerValid = fullName.trim().length >= 2 && phone.replace(/\D/g, '').length >= 9 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const submitRegister = () => {
    if (!fullName.trim() || fullName.trim().length < 2) {
      setError('Please enter your name');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setError('Please enter a valid email');
      return;
    }
    if (phone.replace(/\D/g, '').length < 9) {
      setError('Please enter a valid UAE phone number');
      return;
    }
    setError('');
    setStep('otp');
    setTimeout(() => inputsRef.current[0]?.focus(), 60);
  };
  if (!open) return null;
  return <AnimatePresence data-id="element-264">
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4 overflow-y-auto" onClick={onClose} data-id="element-265">
        <motion.div initial={{
        y: 30,
        scale: 0.98
      }} animate={{
        y: 0,
        scale: 1
      }} exit={{
        y: 30,
        scale: 0.98
      }} onClick={e => e.stopPropagation()} className="bg-white rounded-3xl shadow-2xl max-w-md w-full max-h-[90vh] flex flex-col overflow-hidden my-auto" data-id="element-266">
          {/* Header */}
          <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-brand-50/40 to-white" data-id="element-267">
            <div className="flex items-center gap-3 min-w-0" data-id="element-268">
              <div className="w-10 h-10 rounded-2xl orange-gradient-bg flex items-center justify-center shrink-0" data-id="element-269">
                {step === 'phone' && <Phone className="w-5 h-5 text-white" data-id="element-270" />}
                {step === 'register' && <Sparkles className="w-5 h-5 text-white" data-id="element-271" />}
                {step === 'otp' && <Shield className="w-5 h-5 text-white" data-id="element-272" />}
                {step === 'done' && <CheckCircle2 className="w-5 h-5 text-white" data-id="element-273" />}
              </div>
              <div className="min-w-0" data-id="element-274">
                <h3 className="font-bold text-gray-900" data-id="element-275">
                  {step === 'phone' && (mode === 'signin' ? title : 'Welcome to Pair2')}
                  {step === 'register' && 'Create your account'}
                  {step === 'otp' && 'Enter verification code'}
                  {step === 'done' && (welcomeName ? `Welcome, ${welcomeName}!` : "You're signed in")}
                </h3>
                <p className="text-xs text-gray-500 truncate" data-id="element-276">
                  {step === 'phone' && subtitle}
                  {step === 'register' && 'Quick form, then a one-time SMS code.'}
                  {step === 'otp' && `Code sent to +971 ${phone}`}
                  {step === 'done' && 'Continuing where you left off…'}
                </p>
              </div>
            </div>
            <button onClick={onClose} className="w-9 h-9 rounded-lg hover:bg-white text-gray-500 flex items-center justify-center shrink-0" aria-label="Close" data-id="element-277">
              <X className="w-4 h-4" data-id="element-278" />
            </button>
          </div>

          {/* Body */}
          <div className="p-6 overflow-y-auto flex-1" data-id="element-279">
            {step === 'phone' && <>
                <label className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2 block" data-id="element-280">
                  Mobile number
                </label>
                <div className="flex gap-2 mb-2" data-id="element-281">
                  <div className="flex items-center gap-2 px-3 py-3 bg-gray-50 border border-gray-200 rounded-xl shrink-0" data-id="element-282">
                    <span className="text-base" data-id="element-283">🇦🇪</span>
                    <span className="font-bold text-gray-900 text-sm" data-id="element-284">
                      +971
                    </span>
                  </div>
                  <input type="tel" inputMode="numeric" autoFocus value={phone} onChange={e => {
                setError('');
                setPhone(e.target.value.replace(/[^\d\s]/g, '').slice(0, 12));
              }} placeholder="50 123 4567" className="flex-1 px-4 py-3 rounded-xl border border-gray-200 outline-none text-base focus:border-brand-300 font-medium" data-id="element-285" />
                </div>
                {error && <p className="text-xs text-rose-600 font-medium mb-2" data-id="element-286">
                    {error}
                  </p>}
                <p className="text-[10px] text-gray-400 leading-relaxed mb-5" data-id="element-287">
                  We'll send you a one-time 6-digit code by SMS. Standard SMS
                  rates may apply.
                </p>
                <button onClick={sendOtp} disabled={!phoneValid} className="w-full py-3.5 bg-gray-900 hover:bg-black disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-2xl font-bold text-base flex items-center justify-center gap-2 transition-colors" data-id="element-288">
                  Send code <ArrowRight className="w-4 h-4" data-id="element-289" />
                </button>
                <div className="text-center mt-4 text-[10px] text-gray-400" data-id="element-290">
                  By continuing you agree to Pair2's Terms & Privacy.
                </div>
              </>}

            {step === 'otp' && <>
                <label className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3 block text-center" data-id="element-291">
                  6-digit code
                </label>
                <div className="flex gap-2 justify-center mb-4" data-id="element-292">
                  {otp.map((d, i) => <input key={i} ref={el => inputsRef.current[i] = el} type="tel" inputMode="numeric" maxLength={1} value={d} onChange={e => updateOtp(i, e.target.value)} onKeyDown={e => onOtpKey(i, e)} onPaste={onOtpPaste} className="w-10 sm:w-11 h-12 text-center text-xl font-display font-bold bg-gray-50 border-2 border-gray-200 focus:border-brand-400 focus:bg-white outline-none rounded-xl transition-colors" data-id="element-293" />)}
                </div>
                {error && <p className="text-xs text-rose-600 font-medium mb-2 text-center" data-id="element-294">
                    {error}
                  </p>}
                <p className="text-[11px] text-center text-gray-500 mb-5" data-id="element-295">
                  Didn't get it?{' '}
                  <button onClick={sendOtp} className="font-bold text-brand-600 hover:text-brand-700" data-id="element-296">
                    Resend code
                  </button>
                </p>
                <div className="flex gap-2" data-id="element-297">
                  <button onClick={() => setStep('phone')} className="px-4 py-3.5 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 rounded-2xl font-semibold text-sm flex items-center justify-center gap-1.5" data-id="element-298">
                    <ArrowLeft className="w-4 h-4" data-id="element-299" />
                    Back
                  </button>
                  <button onClick={verifyOtp} className="flex-1 py-3.5 bg-gray-900 hover:bg-black text-white rounded-2xl font-bold text-base flex items-center justify-center gap-2 transition-colors" data-id="element-300">
                    Verify & continue <ArrowRight className="w-4 h-4" data-id="element-301" />
                  </button>
                </div>
                <p className="text-[10px] text-gray-400 text-center mt-4" data-id="element-302">
                  Demo mode — any 6-digit code will work.
                </p>
              </>}

            {step === 'name' && <>
                <label className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2 block" data-id="element-303">
                  What should we call you?
                </label>
                <input type="text" autoFocus value={name} onChange={e => setName(e.target.value.slice(0, 30))} placeholder="e.g. Ahmed" onKeyDown={e => {
              if (e.key === 'Enter') finishSignIn(name);
            }} className="w-full px-4 py-3 rounded-xl border border-gray-200 outline-none text-base focus:border-brand-300 font-medium mb-5" data-id="element-304" />
                <div className="flex gap-2" data-id="element-305">
                  <button onClick={() => finishSignIn('')} className="px-4 py-3.5 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 rounded-2xl font-semibold text-sm" data-id="element-306">
                    Skip
                  </button>
                  <button onClick={() => finishSignIn(name)} className="flex-1 py-3.5 bg-gray-900 hover:bg-black text-white rounded-2xl font-bold text-base flex items-center justify-center gap-2 transition-colors" data-id="element-307">
                    {name.trim() ? `Continue as ${name.trim()}` : 'Continue'}{' '}
                    <ArrowRight className="w-4 h-4" data-id="element-308" />
                  </button>
                </div>
                <p className="text-[10px] text-gray-400 text-center mt-4" data-id="element-309">
                  Only used to greet you — you can change this later.
                </p>
              </>}

            {step === 'done' && <div className="py-6 text-center" data-id="element-310">
                <motion.div initial={{
              scale: 0.5,
              opacity: 0
            }} animate={{
              scale: 1,
              opacity: 1
            }} className="w-16 h-16 rounded-2xl bg-green-100 text-green-600 flex items-center justify-center mx-auto mb-3" data-id="element-311">
                  <Sparkles className="w-7 h-7" data-id="element-312" />
                </motion.div>
                <p className="text-base text-gray-700 font-semibold mb-1" data-id="element-313">
                  {name.trim() ? `Welcome, ${name.trim()}! 👋` : 'Welcome to Pair2 👋'}
                </p>
                <p className="text-xs text-gray-500" data-id="element-314">
                  Glad to have you. Let's move something.
                </p>
              </div>}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>;
  return typeof document !== 'undefined' ? createPortal(modal, document.body) : modal;
}